import { BaseService, ServiceResponse, PaginatedResponse } from './base.service';
import { ClubCommunityOptimizedService } from './club-community-optimized.service';
import type {
  UnifiedMessage,
  UnifiedConversation,
  UnifiedSendMessageRequest,
  UnifiedMessageFilters,
  UnifiedConversationFilters,
  UnifiedMessageSubscriptionCallback,
  UnifiedTypingSubscriptionCallback,
  UnifiedTypingIndicator,
  MessageStatus,
  MessageReaction,
  MessageMention
} from '../../types/unified-messaging';
import type {
  ClubChannel,
  ClubChannelMessage,
  SendMessageRequest as ClubSendMessageRequest,
  UserProfile
} from '../../types/club-community';
import type { Conversation, DirectMessage } from './messaging.service';

/**
 * Unified Messaging Service
 * Extends ClubCommunityOptimizedService to handle both club channels and direct messages
 * Uses the same robust infrastructure for both message types
 */
export class UnifiedMessagingService extends ClubCommunityOptimizedService {
  private static unifiedInstance: UnifiedMessagingService;
  private unifiedSubscriptions: Map<string, any> = new Map();

  static getInstance(): UnifiedMessagingService {
    if (!UnifiedMessagingService.unifiedInstance) {
      UnifiedMessagingService.unifiedInstance = new UnifiedMessagingService();
    }
    return UnifiedMessagingService.unifiedInstance;
  }

  // =====================
  // UNIFIED MESSAGE SENDING
  // =====================

  /**
   * Send a message to either a club channel or direct conversation
   */
  async sendUnifiedMessage(data: UnifiedSendMessageRequest): Promise<ServiceResponse<UnifiedMessage>> {
    return this.withErrorHandling(async () => {

      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('User not authenticated');

      // Validate that either channel_id or conversation_id is provided
      if (!data.channel_id && !data.conversation_id) {
        throw new Error('Either channel_id or conversation_id must be provided');
      }

      if (data.channel_id && data.conversation_id) {
        throw new Error('Cannot provide both channel_id and conversation_id');
      }

      // Prepare message data
      const messageData = {
        sender_id: user.id,
        content: data.content,
        message_type: data.message_type || 'text',
        message_data: data.message_data || {},
        reply_to_id: data.reply_to_id,
        ...(data.channel_id && { channel_id: data.channel_id }),
        ...(data.conversation_id && { conversation_id: data.conversation_id })
      };

      // Insert message into the appropriate table
      let message;
      let error;

      if (data.conversation_id) {
        // Direct message - use direct_messages table
        const { data: dmMessage, error: dmError } = await this.supabase
          .from('direct_messages')
          .insert({
            conversation_id: data.conversation_id,
            sender_id: user.id,
            content: data.content,
            message_type: data.message_type || 'text',
            message_data: data.message_data || {},
            reply_to_id: data.reply_to_id
          })
          .select(`
            *,
            sender:profiles(id, display_name, nickname, first_name, last_name, avatar_url, handicap)
          `)
          .single();

        message = dmMessage;
        error = dmError;
      } else {
        // Channel message - use club_channel_messages table
        const { data: channelMessage, error: channelError } = await this.supabase
          .from('club_channel_messages')
          .insert(messageData)
          .select(`
            *,
            sender:profiles(id, display_name, nickname, first_name, last_name, avatar_url, handicap)
          `)
          .single();

        message = channelMessage;
        error = channelError;
      }

      if (error) throw error;

      // Send push notification for direct messages (non-blocking)
      if (data.conversation_id) {
        (async () => {
          try {
            // Get conversation participants to notify (from conversation_participants table)
            const { data: participants } = await this.supabase
              .from('conversation_participants')
              .select('user_id')
              .eq('conversation_id', data.conversation_id)
              .eq('is_active', true);

            if (participants && participants.length > 0) {
              const recipientIds = participants
                .map(p => p.user_id)
                .filter((id: string) => id !== user.id);

              if (recipientIds.length > 0) {
                const { NotificationsService } = await import('./notifications.service');
                const notificationsService = new NotificationsService();

                // Get sender info for notification
                const { data: senderProfile } = await this.supabase
                  .from('profiles')
                  .select('display_name, first_name')
                  .eq('id', user.id)
                  .single();

                const senderName = senderProfile?.display_name || senderProfile?.first_name || 'Someone';

                await notificationsService.sendBulkNotifications({
                  user_ids: recipientIds,
                  title: 'New Message',
                  body: `${senderName}: ${data.content.substring(0, 100)}${data.content.length > 100 ? '...' : ''}`,
                  notification_type: 'direct_message',
                  data: {
                    conversation_id: data.conversation_id,
                    sender_id: user.id,
                    sender_name: senderName
                  }
                });

                console.log(`✅ Direct message notification sent to ${recipientIds.length} user(s)`);
              }
            }
          } catch (notifError) {
            console.error('❌ Failed to send direct message notification:', notifError);
          }
        })();
      }

      // Handle mentions if provided
      if (data.mentions && data.mentions.length > 0) {
        await this.createMessageMentions(message.id, data.mentions);
      }

      // Create message status for participants
      await this.createMessageStatus(message.id, data.channel_id, data.conversation_id);

      // Contact history logging disabled - not needed for direct messages
      // If you want to enable Member360 contact history tracking, uncomment the line below
      // this.logToContactHistoryIfRelevant(message, data.conversation_id).catch(err => {
      //   console.error('❌ [CONTACT-HISTORY] Background logging failed:', err);
      // });

      // Transform to unified format
      return this.transformToUnifiedMessage(message);
    });
  }

  // =====================
  // UNIFIED CONVERSATION MANAGEMENT
  // =====================

  /**
   * Get all conversations (direct messages and group chats only) for the current user
   * Club channels are excluded - they should be accessed through club-specific interfaces
   */
  async getUnifiedConversations(filters?: UnifiedConversationFilters): Promise<ServiceResponse<UnifiedConversation[]>> {
    return this.withErrorHandling(async () => {
      // Only get direct conversations (personal messages and private groups)
      // Club channels are excluded from the messages page
      const directConversations = await this.getUserDirectConversations();

      // Check if the call failed
      if (!directConversations.success) {
        console.error('❌ [UnifiedMessagingService] getUserDirectConversations failed:', directConversations.error);
        throw new Error(directConversations.error || 'Failed to load direct conversations');
      }

      // Transform direct conversations to unified format
      // _unread_count is pre-computed by getUserDirectConversations (no extra query needed)
      const unifiedConversations = (directConversations.data || []).map(conv => {
        const unified = this.transformConversationToUnified(conv);
        unified.unread_count = (conv as any)._unread_count ?? 0;
        return unified;
      });

      // Sort by last message time, then by creation time
      const sortedConversations = unifiedConversations.sort((a, b) => {
        const aTime = a.last_message_at || a.created_at;
        const bTime = b.last_message_at || b.created_at;
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });

      console.log('✅ [UnifiedMessagingService] getUnifiedConversations success:', {
        conversationCount: sortedConversations.length,
        conversationIds: sortedConversations.map(c => c.id)
      });

      // Apply filters if provided
      if (filters) {
        return this.applyConversationFilters(sortedConversations, filters);
      }

      return sortedConversations;
    });
  }

  /**
   * Get a single conversation by ID (optimized - doesn't load all conversations)
   */
  async getUnifiedConversation(conversationId: string): Promise<ServiceResponse<UnifiedConversation>> {
    return this.withErrorHandling(async () => {
      // Single query — last_message and unread_count not needed for the chat view
      const conversationResult = await this.supabase
        .from('conversations')
        .select(`
          *,
          participants:conversation_participants(
            user_id,
            is_active,
            user:profiles(id, display_name, nickname, first_name, last_name, avatar_url)
          )
        `)
        .eq('id', conversationId)
        .single();

      if (conversationResult.error) {
        if (import.meta.env.DEV) {
          console.error('❌ [UnifiedMessagingService] Conversation query error:', {
            error: conversationResult.error,
            conversationId
          });
        }
        throw conversationResult.error;
      }

      const conversation = conversationResult.data;
      if (!conversation) {
        throw new Error('Conversation not found');
      }

      return this.transformConversationToUnified(conversation);
    });
  }

  /**
   * Get messages for either a club channel or direct conversation
   */
  async getUnifiedMessages(filters: UnifiedMessageFilters): Promise<ServiceResponse<UnifiedMessage[]>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('User not authenticated');

      if (!filters.channel_id && !filters.conversation_id) {
        throw new Error('Either channel_id or conversation_id must be provided');
      }

      const reactionsTable = filters.conversation_id ? 'direct_message_reactions' : 'club_message_reactions';

      // Build messages query
      let messagesQuery: any;
      if (filters.channel_id) {
        messagesQuery = this.supabase
          .from('club_channel_messages')
          .select(`
            *,
            sender:profiles(id, display_name, nickname, first_name, last_name, avatar_url, handicap),
            reply_to:club_channel_messages!reply_to_id(
              id, content, sender_id, sender:profiles(id, display_name, nickname, first_name, last_name, avatar_url)
            )
          `)
          .eq('channel_id', filters.channel_id)
          .order('created_at', { ascending: false })
          .limit(filters.limit || 50);
        if (filters.before_message_id) messagesQuery = messagesQuery.lt('id', filters.before_message_id);
        if (filters.message_type) messagesQuery = messagesQuery.eq('message_type', filters.message_type);
      } else {
        messagesQuery = this.supabase
          .from('direct_messages')
          .select(`*, sender:profiles(id, display_name, nickname, first_name, last_name, avatar_url, handicap)`)
          .eq('conversation_id', filters.conversation_id)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })
          .limit(filters.limit || 50);
        if (filters.offset !== undefined) {
          messagesQuery = messagesQuery.range(filters.offset, filters.offset + (filters.limit || 50) - 1);
        }
        if (filters.before_message_id) messagesQuery = messagesQuery.lt('id', filters.before_message_id);
        if (filters.message_type) messagesQuery = messagesQuery.eq('message_type', filters.message_type);
      }

      // Run messages query first to get IDs, then reactions in parallel with transform
      const messagesResult = await messagesQuery;
      if (messagesResult.error) {
        if (import.meta.env.DEV) console.error('❌ [UnifiedMessagingService] Query error:', messagesResult.error);
        throw messagesResult.error;
      }

      let messages: any[] = messagesResult.data || [];

      if (messages.length > 0) {
        const messageIds = messages.map(m => m.id);
        const { data: reactions } = await this.supabase
          .from(reactionsTable)
          .select('message_id, emoji, user_id')
          .in('message_id', messageIds);

        const reactionsByMessage: Record<string, { counts: Record<string, number>; userReacted: Record<string, boolean> }> = {};
        for (const reaction of reactions || []) {
          if (!reactionsByMessage[reaction.message_id]) {
            reactionsByMessage[reaction.message_id] = { counts: {}, userReacted: {} };
          }
          reactionsByMessage[reaction.message_id].counts[reaction.emoji] =
            (reactionsByMessage[reaction.message_id].counts[reaction.emoji] || 0) + 1;
          if (reaction.user_id === user?.id) {
            reactionsByMessage[reaction.message_id].userReacted[reaction.emoji] = true;
          }
        }

        messages = messages.map(msg => ({
          ...msg,
          reaction_counts: reactionsByMessage[msg.id]?.counts || {},
          user_has_reacted: reactionsByMessage[msg.id]?.userReacted || {}
        }));
      }

      return messages.map(msg => this.transformToUnifiedMessage(msg));
    });
  }

  // =====================
  // UNIFIED REAL-TIME SUBSCRIPTIONS
  // =====================

  /**
   * Subscribe to unified messages
   */
  subscribeToUnifiedMessages(
    conversationId: string,
    type: 'channel' | 'direct',
    callback: UnifiedMessageSubscriptionCallback
  ) {
    const subscriptionKey = `unified-${type}-${conversationId}`;

    // Build filter and table based on type
    const table = type === 'channel' ? 'club_channel_messages' : 'direct_messages';
    const filter = type === 'channel'
      ? `channel_id=eq.${conversationId}`
      : `conversation_id=eq.${conversationId}`;

    // Guard: remove any existing subscription for this key before creating a new one
    const existingSubscription = this.unifiedSubscriptions.get(subscriptionKey);
    if (existingSubscription) {
      this.supabase.removeChannel(existingSubscription);
      this.unifiedSubscriptions.delete(subscriptionKey);
    }

    const enrichWithSender = async (raw: any): Promise<any> => {
      if (raw.sender_id && !raw.sender) {
        const { data: sender } = await this.supabase
          .from('profiles')
          .select('id, display_name, nickname, first_name, last_name, avatar_url, handicap')
          .eq('id', raw.sender_id)
          .single();
        return { ...raw, sender };
      }
      return raw;
    };

    const subscription = this.supabase
      .channel(`unified-messages-${subscriptionKey}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table,
        filter
      }, async (payload) => {
        const enriched = await enrichWithSender(payload.new as any);
        const message = this.transformToUnifiedMessage(enriched);
        callback(message);
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table,
        filter
      }, async (payload) => {
        const enriched = await enrichWithSender(payload.new as any);
        const message = this.transformToUnifiedMessage(enriched);
        callback(message);
      })
      .subscribe();

    this.unifiedSubscriptions.set(subscriptionKey, subscription);
    return subscription;
  }

  /**
   * Unsubscribe from unified messages
   */
  unsubscribeFromUnifiedMessages(conversationId: string, type: 'channel' | 'direct') {
    const subscriptionKey = `unified-${type}-${conversationId}`;
    const subscription = this.unifiedSubscriptions.get(subscriptionKey);
    
    if (subscription) {
      this.supabase.removeChannel(subscription);
      this.unifiedSubscriptions.delete(subscriptionKey);
    }
  }

  /**
   * Subscribe to typing indicators
   */
  subscribeToUnifiedTyping(
    conversationId: string,
    type: 'channel' | 'direct',
    callback: UnifiedTypingSubscriptionCallback
  ) {
    const channelName = `unified-typing-${type}-${conversationId}`;

    const subscription = this.supabase
      .channel(channelName)
      .on('broadcast', { event: 'typing' }, (payload) => {
        callback(payload.payload as UnifiedTypingIndicator);
      })
      .subscribe();

    this.unifiedSubscriptions.set(`typing-${channelName}`, subscription);
    return subscription;
  }

  /**
   * Unsubscribe from typing indicators
   */
  unsubscribeFromUnifiedTyping(conversationId: string, type: 'channel' | 'direct') {
    const channelName = `unified-typing-${type}-${conversationId}`;
    const subscriptionKey = `typing-${channelName}`;
    const subscription = this.unifiedSubscriptions.get(subscriptionKey);

    if (subscription) {
      this.supabase.removeChannel(subscription);
      this.unifiedSubscriptions.delete(subscriptionKey);
    }
  }

  /**
   * Subscribe to message reactions
   */
  subscribeToUnifiedReactions(
    callback: (payload: { messageId: string; reactions: any[]; reactionCounts: Record<string, number> }) => void
  ) {
    const subscription = this.supabase
      .channel('unified-reactions')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'club_message_reactions'
      }, async (payload) => {
        // Get updated reactions for the message
        if (payload.new) {
          const messageId = payload.new.message_id;
          const response = await this.getUnifiedMessageReactions(messageId);
          if (response.success && response.data) {
            callback({
              messageId,
              reactions: response.data.reactions,
              reactionCounts: response.data.reactionCounts
            });
          }
        }
      })
      .subscribe();

    this.unifiedSubscriptions.set('reactions', subscription);
    return subscription;
  }

  // =====================
  // MESSAGE STATUS & REACTIONS
  // =====================

  /**
   * Mark message as read
   */
  async markUnifiedMessageAsRead(messageId: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('User not authenticated');

      const { error } = await this.supabase
        .from('message_status')
        .upsert({
          message_id: messageId,
          user_id: user.id,
          status: 'read',
          timestamp: new Date().toISOString()
        });

      if (error) throw error;
    });
  }

  /**
   * Mark all messages in a conversation as read
   * Simple approach like notifications: set read = true
   */
  async markConversationAsRead(conversationId: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('User not authenticated');

      // Mark all messages as read (like notifications: simple read boolean)
      const { error } = await this.supabase
        .from('direct_messages')
        .update({ read: true })
        .eq('conversation_id', conversationId)
        .neq('sender_id', user.id)
        .eq('read', false);

      // Silently ignore if 'read' column doesn't exist (migration not run)
      if (error && !error.message?.includes('read')) {
        console.warn('⚠️ Failed to mark conversation as read:', error);
      }
    });
  }

  /**
   * Helper to determine which table a message is in
   */
  private async getMessageTableType(messageId: string): Promise<'channel' | 'direct'> {
    // Check direct_messages first (more common for current usage)
    const { data: directMsg } = await this.supabase
      .from('direct_messages')
      .select('id')
      .eq('id', messageId)
      .maybeSingle();

    if (directMsg) return 'direct';

    // Check club_channel_messages
    const { data: channelMsg } = await this.supabase
      .from('club_channel_messages')
      .select('id')
      .eq('id', messageId)
      .maybeSingle();

    if (channelMsg) return 'channel';

    throw new Error('Message not found');
  }

  /**
   * Add reaction to message (supports both channel and direct messages)
   */
  async addUnifiedReaction(messageId: string, emoji: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('User not authenticated');

      // Determine which reactions table to use
      const messageType = await this.getMessageTableType(messageId);
      const reactionsTable = messageType === 'channel' ? 'club_message_reactions' : 'direct_message_reactions';

      const { error } = await this.supabase
        .from(reactionsTable)
        .upsert({
          message_id: messageId,
          user_id: user.id,
          emoji,
          created_at: new Date().toISOString()
        }, {
          onConflict: 'message_id,user_id,emoji',
          ignoreDuplicates: false
        });

      if (error) throw error;
    });
  }

  /**
   * Remove reaction from message (supports both channel and direct messages)
   */
  async removeUnifiedReaction(messageId: string, emoji: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('User not authenticated');

      // Determine which reactions table to use
      const messageType = await this.getMessageTableType(messageId);
      const reactionsTable = messageType === 'channel' ? 'club_message_reactions' : 'direct_message_reactions';

      const { error } = await this.supabase
        .from(reactionsTable)
        .delete()
        .eq('message_id', messageId)
        .eq('user_id', user.id)
        .eq('emoji', emoji);

      if (error) throw error;
    });
  }

  /**
   * Edit message (supports both channel messages and direct messages)
   */
  async editUnifiedMessage(messageId: string, newContent: string): Promise<ServiceResponse<UnifiedMessage>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('User not authenticated');

      // Try direct_messages first (most common for staff-member conversations)
      const { data: directData, error: directError } = await this.supabase
        .from('direct_messages')
        .update({
          content: newContent,
          edited_at: new Date().toISOString()
        })
        .eq('id', messageId)
        .eq('sender_id', user.id) // Only allow editing own messages
        .select('*')
        .maybeSingle();

      if (directData) {
        return this.transformToUnifiedMessage(directData);
      }

      // If not found in direct_messages, try club_channel_messages
      const { data: channelData, error: channelError } = await this.supabase
        .from('club_channel_messages')
        .update({
          content: newContent,
          edited_at: new Date().toISOString()
        })
        .eq('id', messageId)
        .eq('sender_id', user.id) // Only allow editing own messages
        .select('*')
        .maybeSingle();

      if (channelError) throw channelError;
      if (!channelData) throw new Error('Message not found or not editable');

      return this.transformToUnifiedMessage(channelData);
    });
  }

  /**
   * Delete message (supports both channel messages and direct messages)
   */
  async deleteUnifiedMessage(messageId: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('User not authenticated');

      // Try direct_messages first (most common for staff-member conversations)
      const { data: directData, error: directError } = await this.supabase
        .from('direct_messages')
        .update({
          deleted_at: new Date().toISOString()
        })
        .eq('id', messageId)
        .eq('sender_id', user.id) // Only allow deleting own messages
        .select('id')
        .maybeSingle();

      if (directData) {
        return; // Successfully deleted from direct_messages
      }

      // If not found in direct_messages, try club_channel_messages
      const { error: channelError } = await this.supabase
        .from('club_channel_messages')
        .update({
          deleted_at: new Date().toISOString()
        })
        .eq('id', messageId)
        .eq('sender_id', user.id); // Only allow deleting own messages

      if (channelError) throw channelError;
    });
  }

  /**
   * Get message reactions with counts
   */
  async getUnifiedMessageReactions(messageId: string): Promise<ServiceResponse<{
    reactions: MessageReaction[];
    reactionCounts: Record<string, number>;
    userReactions: Record<string, boolean>;
  }>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('User not authenticated');

      const { data: reactions, error } = await this.supabase
        .from('club_message_reactions')
        .select(`
          *,
          user:profiles!club_message_reactions_user_id_fkey(
            id,
            display_name,
            avatar_url
          )
        `)
        .eq('message_id', messageId);

      if (error) throw error;

      // Calculate reaction counts
      const reactionCounts: Record<string, number> = {};
      const userReactions: Record<string, boolean> = {};

      reactions?.forEach(reaction => {
        reactionCounts[reaction.emoji] = (reactionCounts[reaction.emoji] || 0) + 1;
        if (reaction.user_id === user.id) {
          userReactions[reaction.emoji] = true;
        }
      });

      return {
        reactions: reactions || [],
        reactionCounts,
        userReactions
      };
    });
  }

  // =====================
  // HELPER METHODS
  // =====================

  /**
   * Apply consistent name resolution logic (same as tournament code)
   */
  private resolveUserDisplayName(userProfile: any): string {
    if (!userProfile) return 'Unknown User';

    return userProfile.display_name ||
      (userProfile.first_name && userProfile.last_name
        ? `${userProfile.first_name} ${userProfile.last_name}`
        : userProfile.first_name || 'Unknown User');
  }

  /**
   * Transform database message to unified format
   */
  private transformToUnifiedMessage(dbMessage: any): UnifiedMessage {
    // Apply consistent name resolution to sender
    const resolvedSender = dbMessage.sender ? {
      ...dbMessage.sender,
      display_name: this.resolveUserDisplayName(dbMessage.sender)
    } : dbMessage.sender;

    return {
      id: dbMessage.id,
      content: dbMessage.content,
      message_type: dbMessage.message_type,
      message_data: dbMessage.message_data || {},
      sender_id: dbMessage.sender_id,
      created_at: dbMessage.created_at,
      updated_at: dbMessage.created_at, // Use created_at since updated_at doesn't exist
      edited_at: dbMessage.edited_at,
      deleted_at: dbMessage.deleted_at,
      sender: resolvedSender,
      channel_id: dbMessage.channel_id,
      conversation_id: dbMessage.conversation_id,
      message_context: dbMessage.channel_id ? 'channel' : 'direct',
      reply_to_id: dbMessage.reply_to_id,
      reply_to: dbMessage.reply_to ? this.transformToUnifiedMessage(dbMessage.reply_to) : undefined,
      reactions: dbMessage.reactions || [],
      mentions: dbMessage.mentions || [],
      reaction_counts: dbMessage.reaction_counts || {},
      user_has_reacted: dbMessage.user_has_reacted || {},
      status: dbMessage.status || []
    };
  }

  /**
   * Transform club channel to unified conversation
   */
  private transformClubChannelToUnified(channel: ClubChannel): UnifiedConversation {
    return {
      id: channel.id,
      type: 'channel',
      title: channel.name,
      description: channel.description,
      club_id: channel.club_id,
      channel_type: channel.channel_type,
      is_private: channel.is_private,
      is_default: channel.is_default,
      position: channel.position,
      created_at: channel.created_at,
      updated_at: channel.updated_at,
      created_by: channel.created_by,
      last_message: channel.last_message ? this.transformToUnifiedMessage(channel.last_message) : undefined,
      unread_count: channel.unread_count || 0,
      display_name: channel.name,
      initials: channel.name.charAt(0).toUpperCase()
    };
  }

  /**
   * Transform direct conversation to unified conversation
   */
  private transformConversationToUnified(conversation: any): UnifiedConversation {
    // Determine the correct type based on conversation_type and participant count
    let type: 'direct' | 'group' = 'direct';
    if (conversation.conversation_type === 'group' || 
        (conversation.participants && conversation.participants.length > 2)) {
      type = 'group';
    }
    
    return {
      id: conversation.id,
      type: type,
      title: conversation.title,
      description: conversation.description,
      conversation_type: conversation.conversation_type as 'direct' | 'group',
      created_at: conversation.created_at,
      updated_at: conversation.updated_at,
      created_by: conversation.created_by,
      last_message_at: conversation.last_message_at,
      last_message: conversation.last_message ? this.transformToUnifiedMessage(conversation.last_message) : undefined,
      unread_count: conversation.unread_count || 0,
      participants: conversation.participants?.map((p: any) => {
        if (!p.user) return null;
        return {
          ...p.user,
          display_name: this.resolveUserDisplayName(p.user)
        };
      }).filter(Boolean) || [],
      display_name: conversation.title || (type === 'group' ? 'Group Chat' : 'Direct Message'),
      initials: conversation.title?.charAt(0).toUpperCase() || (type === 'group' ? 'G' : 'D')
    };
  }

  /**
   * Merge and sort club channels and direct conversations
   */
  private mergeAndSortConversations(
    clubChannels: ClubChannel[],
    directConversations: Conversation[]
  ): UnifiedConversation[] {
    const unifiedChannels = clubChannels.map(channel => this.transformClubChannelToUnified(channel));
    const unifiedDirect = directConversations.map(conv => this.transformConversationToUnified(conv));
    
    const allConversations = [...unifiedChannels, ...unifiedDirect];
    
    // Sort by last message time, then by creation time
    return allConversations.sort((a, b) => {
      const aTime = a.last_message_at || a.created_at;
      const bTime = b.last_message_at || b.created_at;
      return new Date(bTime).getTime() - new Date(aTime).getTime();
    });
  }

  /**
   * Apply filters to conversations
   */
  private applyConversationFilters(
    conversations: UnifiedConversation[],
    filters: UnifiedConversationFilters
  ): UnifiedConversation[] {
    let filtered = conversations;

    if (filters.type) {
      filtered = filtered.filter(conv => conv.type === filters.type);
    }

    if (filters.club_id) {
      filtered = filtered.filter(conv => conv.club_id === filters.club_id);
    }

    return filtered;
  }

  /**
   * Get user's club channels
   */
  private async getUserClubChannels(): Promise<ServiceResponse<ClubChannel[]>> {
    // Use the existing club community service method
    const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
    if (!user) throw new Error('User not authenticated');

    const { data: memberships } = await this.supabase
      .from('user_club_memberships')
      .select('club_id')
      .eq('user_id', user.id)
      .eq('status', 'active');

    if (!memberships || memberships.length === 0) {
      return { success: true, data: [], error: undefined };
    }

    const clubIds = memberships.map(m => m.club_id);
    const allChannels: ClubChannel[] = [];

    for (const clubId of clubIds) {
      const channels = await this.getClubChannelsOptimized(clubId);
      if (channels.success && channels.data) {
        allChannels.push(...channels.data);
      }
    }

    return { success: true, data: allChannels, error: undefined };
  }

  /**
   * Get user's direct conversations.
   * Optimized: single round-trip to get conversation IDs, then Q2/Q3/Q4 run in parallel.
   */
  private async getUserDirectConversations(): Promise<ServiceResponse<Conversation[]>> {
    try {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('User not authenticated');

      // Q1+Q2 merged: query conversation_participants and embed conversation + all participants in one round-trip
      // This eliminates the serial Q1 → Q2 waterfall
      const [myParticipantRows, unreadResult] = await Promise.all([
        this.supabase
          .from('conversation_participants')
          .select(`
            conversation_id,
            conversation:conversations(
              *,
              participants:conversation_participants(
                user_id,
                is_active,
                user:profiles(id, display_name, nickname, first_name, last_name, avatar_url)
              )
            )
          `)
          .eq('user_id', user.id)
          .eq('is_active', true),

        // Unread counts for the user's conversations (no conversation_id filter needed — we'll join after)
        this.supabase
          .from('direct_messages')
          .select('conversation_id')
          .neq('sender_id', user.id)
          .eq('read', false),
      ]);

      if (myParticipantRows.error) {
        if (import.meta.env.DEV) {
          console.error('❌ [UnifiedMessagingService] Error fetching user conversations:', myParticipantRows.error);
        }
        throw myParticipantRows.error;
      }

      if (!myParticipantRows.data?.length) {
        return { success: true, data: [], error: undefined };
      }

      // Extract conversation objects (conversation is embedded as single object)
      const conversationMap = new Map<string, any>();
      for (const row of myParticipantRows.data) {
        const conv = row.conversation as any;
        if (conv && !conversationMap.has(conv.id)) {
          conversationMap.set(conv.id, conv);
        }
      }
      const conversationIds = Array.from(conversationMap.keys());

      // Q3: last message per conversation
      const lastMessagesResult = await this.supabase
        .from('direct_messages')
        .select(`
          id,
          content,
          message_type,
          created_at,
          sender_id,
          conversation_id,
          sender:profiles(id, display_name, nickname, first_name, last_name, avatar_url)
        `)
        .in('conversation_id', conversationIds)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(conversationIds.length * 3);

      if (lastMessagesResult.error) {
        console.warn('⚠️ [UnifiedMessagingService] Error fetching last messages (non-critical):', lastMessagesResult.error);
      }

      // Build last-message map (first hit per conversation_id = most recent)
      const lastMessagesMap = new Map<string, any>();
      for (const msg of lastMessagesResult.data || []) {
        if (msg?.conversation_id && !lastMessagesMap.has(msg.conversation_id)) {
          lastMessagesMap.set(msg.conversation_id, msg);
        }
      }

      // Build unread count map (filter to only this user's conversations)
      const conversationIdSet = new Set(conversationIds);
      const unreadCounts: Record<string, number> = {};
      for (const msg of unreadResult.data || []) {
        if (conversationIdSet.has(msg.conversation_id)) {
          unreadCounts[msg.conversation_id] = (unreadCounts[msg.conversation_id] || 0) + 1;
        }
      }

      const transformedConversations: Conversation[] = Array.from(conversationMap.values()).map(conv => ({
        ...conv,
        conversation_type: conv.conversation_type as 'direct' | 'group',
        participants: conv.participants?.map((p: any) => ({ ...p, user: p.user })) || [],
        last_message: lastMessagesMap.get(conv.id) || null,
        _unread_count: unreadCounts[conv.id] || 0,
      }));

      // Sort by last message time, then creation time
      transformedConversations.sort((a, b) => {
        const aTime = (a as any).last_message_at || a.created_at;
        const bTime = (b as any).last_message_at || b.created_at;
        return new Date(bTime).getTime() - new Date(aTime).getTime();
      });

      return { success: true, data: transformedConversations, error: undefined };
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('❌ [UnifiedMessagingService] getUserDirectConversations error:', error);
      }
      return { success: false, data: null, error: error?.message || 'Failed to load conversations' };
    }
  }

  /**
   * Create message status for all participants
   * Note: message_status table has FK constraint to club_channel_messages only,
   * so we skip status creation for direct messages (conversation_id).
   * Direct message read tracking uses a different mechanism via conversation_participants.last_read_at
   */
  private async createMessageStatus(
    messageId: string,
    channelId?: string,
    conversationId?: string
  ): Promise<void> {
    try {
      // Skip message_status for direct messages - FK constraint only allows club_channel_messages IDs
      // Direct message unread tracking is handled differently
      if (conversationId && !channelId) {
        return;
      }

      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;

      let participantIds: string[] = [];

      if (channelId) {
        // Get club members for channel
        const { data: memberships } = await this.supabase
          .from('user_club_memberships')
          .select('user_id')
          .eq('club_id', (await this.getChannelClubId(channelId)).data)
          .eq('status', 'active');

        participantIds = memberships?.map(m => m.user_id) || [];
      }

      // Create status entries using upsert to avoid conflicts
      const statusInserts = participantIds.map(userId => ({
        message_id: messageId,
        user_id: userId,
        status: userId === user.id ? 'read' : 'sent',
        timestamp: new Date().toISOString()
      }));

      if (statusInserts.length > 0) {
        // Use upsert to handle potential duplicates gracefully
        const { error } = await this.supabase
          .from('message_status')
          .upsert(statusInserts, {
            onConflict: 'message_id,user_id',
            ignoreDuplicates: false
          });

        if (error) {
          console.warn('⚠️ Message status creation warning:', error.message);
        }
      }
    } catch (error) {
      // Catch all errors - message status is not critical for message delivery
      console.warn('⚠️ Message status creation failed:', error);
    }
  }

  /**
   * Get club ID for a channel
   */
  private async getChannelClubId(channelId: string): Promise<ServiceResponse<string>> {
    const { data: channel, error } = await this.supabase
      .from('club_channels')
      .select('club_id')
      .eq('id', channelId)
      .single();

    if (error) throw error;
    return { success: true, data: channel.club_id, error: undefined };
  }

  /**
   * Create message mentions
   */
  private async createMessageMentions(messageId: string, mentionUserIds: string[]): Promise<void> {
    const mentionInserts = mentionUserIds.map(userId => ({
      message_id: messageId,
      mentioned_user_id: userId
    }));

    await this.supabase
      .from('club_message_mentions')
      .insert(mentionInserts);
  }

  /**
   * Search users for new conversations - only friends
   */
  async searchUsers(query: string = '', limit: number = 20): Promise<ServiceResponse<UserProfile[]>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('User not authenticated');

      // Get friend IDs (both directions)
      const { data: friendships, error: friendError } = await this.supabase
        .from('friend_relationships')
        .select('requester_id, addressee_id')
        .eq('status', 'accepted')
        .or(`requester_id.eq.${user.id},addressee_id.eq.${user.id}`);

      if (friendError) throw friendError;

      const friendIds = friendships?.map(friendship =>
        friendship.requester_id === user.id ? friendship.addressee_id : friendship.requester_id
      ) || [];

      if (friendIds.length === 0) {
        return []; // No friends found
      }

      // Now query profiles with the filtered friend IDs
      let queryBuilder = this.supabase
        .from('profiles')
        .select('id, display_name, nickname, first_name, last_name, avatar_url, email')
        .in('id', friendIds)
        .limit(limit);

      if (query.trim()) {
        queryBuilder = queryBuilder.or(`display_name.ilike.%${query}%,nickname.ilike.%${query}%,first_name.ilike.%${query}%,last_name.ilike.%${query}%,email.ilike.%${query}%`);
      }

      const { data, error } = await queryBuilder;

      if (error) throw error;

      return data?.map(profile => ({
        id: profile.id,
        display_name: this.resolveUserDisplayName(profile),
        avatar_url: profile.avatar_url,
        email: profile.email
      })) || [];
    });
  }

  /**
   * Create a new conversation
   */
  async createConversation(data: {
    conversation_type: 'direct' | 'group';
    participant_ids: string[];
    title?: string;
  }): Promise<ServiceResponse<UnifiedConversation>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('User not authenticated');

      // Start a transaction
      const { data: conversation, error: conversationError } = await this.supabase
        .from('conversations')
        .insert({
          conversation_type: data.conversation_type,
          created_by: user.id,
          title: data.title
        })
        .select('*')
        .single();

      if (conversationError) throw conversationError;

      // Add participants including the current user
      const allParticipantIds = [...new Set([user.id, ...data.participant_ids])];

      const participants = allParticipantIds.map(participantId => ({
        conversation_id: conversation.id,
        user_id: participantId,
        is_active: true,
        role: participantId === user.id ? 'admin' : 'member'
      }));

      const { error: participantsError } = await this.supabase
        .from('conversation_participants')
        .insert(participants);

      if (participantsError) {
        console.error('💬 createConversation - participantsError:', participantsError);
        throw participantsError;
      }

      // Get the created conversation with participants
      const { data: fullConversation, error: fetchError } = await this.supabase
        .from('conversations')
        .select(`
          *,
          participants:conversation_participants(
            user_id,
            is_active,
            role,
            user:profiles(id, display_name, nickname, first_name, last_name, avatar_url)
          )
        `)
        .eq('id', conversation.id)
        .single();

      if (fetchError) throw fetchError;

      return this.transformConversationToUnified(fullConversation);
    });
  }

  /**
   * Update conversation details (title, description)
   */
  async updateConversation(conversationId: string, data: {
    title?: string;
    description?: string;
  }): Promise<ServiceResponse<UnifiedConversation>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('User not authenticated');

      // Check if user is admin of the conversation
      const { data: participant, error: participantError } = await this.supabase
        .from('conversation_participants')
        .select('role')
        .eq('conversation_id', conversationId)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (participantError || !participant || participant.role !== 'admin') {
        throw new Error('Only admins can update conversation details');
      }

      // Update conversation
      const { data: conversation, error: updateError } = await this.supabase
        .from('conversations')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', conversationId)
        .select('*')
        .single();

      if (updateError) throw updateError;

      // Get updated conversation with participants
      const { data: fullConversation, error: fetchError } = await this.supabase
        .from('conversations')
        .select(`
          *,
          participants:conversation_participants(
            user_id,
            is_active,
            role,
            user:profiles(id, display_name, nickname, first_name, last_name, avatar_url)
          )
        `)
        .eq('id', conversationId)
        .single();

      if (fetchError) throw fetchError;

      return this.transformConversationToUnified(fullConversation);
    });
  }

  /**
   * Add participants to a group conversation
   */
  async addParticipants(conversationId: string, participantIds: string[]): Promise<ServiceResponse<UnifiedConversation>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('User not authenticated');

      // Check if user is admin of the conversation
      const { data: participant, error: participantError } = await this.supabase
        .from('conversation_participants')
        .select('role')
        .eq('conversation_id', conversationId)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (participantError || !participant || participant.role !== 'admin') {
        throw new Error('Only admins can add participants');
      }

      // Check if conversation is a group
      const { data: conversation, error: convError } = await this.supabase
        .from('conversations')
        .select('conversation_type')
        .eq('id', conversationId)
        .single();

      if (convError || conversation.conversation_type !== 'group') {
        throw new Error('Can only add participants to group conversations');
      }

      // Add new participants
      const participants = participantIds.map(participantId => ({
        conversation_id: conversationId,
        user_id: participantId,
        is_active: true,
        role: 'member'
      }));

      const { error: insertError } = await this.supabase
        .from('conversation_participants')
        .insert(participants);

      if (insertError) throw insertError;

      // Get updated conversation with participants
      const { data: fullConversation, error: fetchError } = await this.supabase
        .from('conversations')
        .select(`
          *,
          participants:conversation_participants(
            user_id,
            is_active,
            role,
            user:profiles(id, display_name, nickname, first_name, last_name, avatar_url)
          )
        `)
        .eq('id', conversationId)
        .single();

      if (fetchError) throw fetchError;

      return this.transformConversationToUnified(fullConversation);
    });
  }

  /**
   * Remove participant from a group conversation
   */
  async removeParticipant(conversationId: string, participantId: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('User not authenticated');

      // Check if user is admin of the conversation
      const { data: participant, error: participantError } = await this.supabase
        .from('conversation_participants')
        .select('role')
        .eq('conversation_id', conversationId)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (participantError || !participant || participant.role !== 'admin') {
        throw new Error('Only admins can remove participants');
      }

      // Check if trying to remove admin
      const { data: targetParticipant, error: targetError } = await this.supabase
        .from('conversation_participants')
        .select('role')
        .eq('conversation_id', conversationId)
        .eq('user_id', participantId)
        .eq('is_active', true)
        .single();

      if (targetError || !targetParticipant) {
        throw new Error('Participant not found');
      }

      if (targetParticipant.role === 'admin') {
        throw new Error('Cannot remove admin from conversation');
      }

      // Remove participant
      const { error: removeError } = await this.supabase
        .from('conversation_participants')
        .update({ is_active: false, left_at: new Date().toISOString() })
        .eq('conversation_id', conversationId)
        .eq('user_id', participantId);

      if (removeError) throw removeError;

      return { success: true, data: undefined, error: undefined } as ServiceResponse<void>;
    });
  }

  /**
   * Delete/Leave a conversation for the current user
   */
  async deleteConversation(conversationId: string): Promise<ServiceResponse<void>> {
    return this.withErrorHandling(async () => {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('User not authenticated');

      // Remove current user as participant (leave conversation)
      // Use .select() to verify we actually updated a row
      const { data: updated, error: leaveError } = await this.supabase
        .from('conversation_participants')
        .update({
          is_active: false,
          left_at: new Date().toISOString()
        })
        .eq('conversation_id', conversationId)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .select('id');

      if (leaveError) throw leaveError;

      if (!updated || updated.length === 0) {
        console.warn('⚠️ No active participant record found, user may already have left');
      }

      // Check how many active participants remain
      const { data: remainingParticipants, error: countError } = await this.supabase
        .from('conversation_participants')
        .select('user_id')
        .eq('conversation_id', conversationId)
        .eq('is_active', true);

      if (countError) {
        console.warn('⚠️ Failed to count remaining participants:', countError);
        return; // User already left, that's the main goal
      }

      // If no active participants remain, clean up the conversation
      if (!remainingParticipants || remainingParticipants.length === 0) {
        // Delete messages first (foreign key constraint)
        const { error: deleteMsgsError } = await this.supabase
          .from('direct_messages')
          .delete()
          .eq('conversation_id', conversationId);

        if (deleteMsgsError) {
          console.warn('⚠️ Failed to delete direct_messages:', deleteMsgsError);
        }

        // Delete all participant records
        const { error: deleteParticipantsError } = await this.supabase
          .from('conversation_participants')
          .delete()
          .eq('conversation_id', conversationId);

        if (deleteParticipantsError) {
          console.warn('⚠️ Failed to delete participants:', deleteParticipantsError);
        }

        // Delete the conversation itself
        const { error: deleteConvError } = await this.supabase
          .from('conversations')
          .delete()
          .eq('id', conversationId);

        if (deleteConvError) {
          console.warn('⚠️ Failed to delete conversation row:', deleteConvError);
        }
      }
    });
  }

  // =====================
  // STAFF-MEMBER CONVERSATION DETECTION
  // =====================

  /**
   * Check if a conversation is a staff-member conversation
   */
  async isStaffMemberConversation(conversationId: string): Promise<{
    isStaffMemberConv: boolean;
    staffMembershipIds: string[];
    memberMembershipIds: string[];
    clubId?: string;
  }> {
    return this.withErrorHandling(async () => {
      // Get conversation with participants
      const { data: conversation, error: convError } = await this.supabase
        .from('conversations')
        .select(`
          id,
          conversation_type,
          title,
          description,
          created_by,
          participants:conversation_participants(
            user_id,
            is_active
          )
        `)
        .eq('id', conversationId)
        .eq('conversation_participants.is_active', true)
        .single();

      if (convError || !conversation) {
        return { isStaffMemberConv: false, staffMembershipIds: [], memberMembershipIds: [] };
      }


      // Check if it's a staff conversation by title/description
      const isStaffConversation = conversation.title === 'Staff Support' ||
                                 conversation.description === 'Support conversation for member';


      if (!isStaffConversation || conversation.conversation_type !== 'group') {
        // Fall back to checking by participants - if we have both staff and members, treat as staff conversation
        const participantUserIds = conversation.participants.map(p => p.user_id);
        const { staffMembershipIds, memberMembershipIds, clubId } = await this.categorizeParticipants(participantUserIds);

        const hasStaffAndMembers = staffMembershipIds.length > 0 && memberMembershipIds.length > 0;

        if (hasStaffAndMembers) {
          return { isStaffMemberConv: true, staffMembershipIds, memberMembershipIds, clubId };
        } else {
          return { isStaffMemberConv: false, staffMembershipIds: [], memberMembershipIds: [] };
        }
      }

      // Get membership details for all participants
      const participantUserIds = conversation.participants.map(p => p.user_id);
      const { staffMembershipIds, memberMembershipIds, clubId } = await this.categorizeParticipants(participantUserIds);

      // Must have both staff and non-staff members
      const isStaffMemberConv = staffMembershipIds.length > 0 && memberMembershipIds.length > 0;

      return { isStaffMemberConv, staffMembershipIds, memberMembershipIds, clubId };
    });
  }

  /**
   * Categorize conversation participants into staff and members
   */
  async categorizeParticipants(userIds: string[]): Promise<{
    staffMembershipIds: string[];
    memberMembershipIds: string[];
    clubId?: string;
  }> {
    if (userIds.length === 0) {
      return { staffMembershipIds: [], memberMembershipIds: [] };
    }

    // Get memberships for all participants
    const { data: memberships, error } = await this.supabase
      .from('user_club_memberships')
      .select('id, user_id, club_id, staff_role')
      .in('user_id', userIds);


    if (error || !memberships) {
      return { staffMembershipIds: [], memberMembershipIds: [] };
    }

    // Group into staff and non-staff
    const staffMembershipIds: string[] = [];
    const memberMembershipIds: string[] = [];
    let clubId: string | undefined;

    memberships.forEach(membership => {
      if (!clubId) clubId = membership.club_id; // Use first club found

      if (membership.staff_role) {
        staffMembershipIds.push(membership.id);
      } else {
        memberMembershipIds.push(membership.id);
      }
    });


    return { staffMembershipIds, memberMembershipIds, clubId };
  }

  /**
   * Determine message direction and get contact type
   */
  async getMessageDirection(senderId: string, staffMembershipIds: string[], memberMembershipIds: string[], messageType: string): Promise<{
    direction: 'staff_to_member' | 'member_to_staff' | 'unknown';
    senderMembershipId?: string;
    recipientMembershipIds: string[];
    contactType: string;
  }> {

    // Get sender's memberships (may have multiple - e.g., both staff and member roles)
    const { data: senderMemberships, error } = await this.supabase
      .from('user_club_memberships')
      .select('id, staff_role')
      .eq('user_id', senderId)
      .in('id', [...staffMembershipIds, ...memberMembershipIds]);

    if (error) {
      console.error('❌ [MESSAGE-DIRECTION] Error querying sender memberships:', error);
      return { direction: 'unknown', recipientMembershipIds: [], contactType: 'unknown_message' };
    }

    if (!senderMemberships || senderMemberships.length === 0) {
      return { direction: 'unknown', recipientMembershipIds: [], contactType: 'unknown_message' };
    }

    // Priority: If sender has staff role, treat as staff-to-member
    const staffMembership = senderMemberships.find(m => staffMembershipIds.includes(m.id));
    const memberMembership = senderMemberships.find(m => memberMembershipIds.includes(m.id));

    if (staffMembership) {
      return {
        direction: 'staff_to_member',
        senderMembershipId: staffMembership.id,
        recipientMembershipIds: memberMembershipIds,
        contactType: this.getContactType('staff_to_member', messageType)
      };
    } else if (memberMembership) {
      return {
        direction: 'member_to_staff',
        senderMembershipId: memberMembership.id,
        recipientMembershipIds: staffMembershipIds,
        contactType: this.getContactType('member_to_staff', messageType)
      };
    }

    return { direction: 'unknown', recipientMembershipIds: [], contactType: 'unknown_message' };
  }

  /**
   * Get appropriate contact type based on direction and message type
   */
  private getContactType(direction: string, messageType: string): string {
    switch (direction) {
      case 'staff_to_member':
        switch (messageType) {
          case 'system': return 'staff_notification';
          case 'round_invite': return 'staff_notification';
          default: return 'staff_message';
        }
      case 'member_to_staff':
        switch (messageType) {
          case 'round_share': return 'member_round_report';
          case 'photo': return 'member_photo_report';
          default: return 'member_inquiry';
        }
      default:
        return 'unknown_message';
    }
  }

  /**
   * Log message to contact history if it's a staff-member conversation
   */
  async logToContactHistoryIfRelevant(message: any, conversationId?: string): Promise<void> {
    if (!conversationId) {
      return; // Only for conversation messages, not channels
    }

    try {
      const { data: { session } } = await this.supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        return;
      }

      // Check if this is a staff-member conversation
      const convResponse = await this.isStaffMemberConversation(conversationId);

      // Unwrap the ServiceResponse
      const convInfo = convResponse.success ? convResponse.data : null;


      if (!convInfo?.isStaffMemberConv || !convInfo?.clubId || !convInfo?.staffMembershipIds || !convInfo?.memberMembershipIds) {
        return;
      }

      // Determine message direction
      const direction = await this.getMessageDirection(
        user.id,
        convInfo.staffMembershipIds,
        convInfo.memberMembershipIds,
        message.message_type || 'text'
      );


      if (direction.direction === 'unknown' || !direction.senderMembershipId) {
        return;
      }

      // Import and use contact history service
      const { contactHistoryService } = await import('./contact-history.service');

      // Log contact history for each recipient in parallel (much faster!)
      await Promise.all(
        direction.recipientMembershipIds.map(recipientId =>
          contactHistoryService.logPrivateMessage({
            messageId: message.id,
            senderMembershipId: direction.senderMembershipId,
            recipientMembershipId: recipientId,
            contactType: direction.contactType as any,
            content: message.content || '',
            clubId: convInfo.clubId
          })
        )
      );
    } catch (error) {
      console.error('❌ [CONTACT-HISTORY] Failed to log contact history for private message:', error);
      // Don't throw - contact history logging shouldn't break message sending
    }
  }

  // =====================
  // CLEANUP
  // =====================

  /**
   * Unsubscribe from all unified subscriptions
   */
  unsubscribeAllUnified() {
    this.unifiedSubscriptions.forEach((subscription, key) => {
      this.supabase.removeChannel(subscription);
    });
    this.unifiedSubscriptions.clear();
  }

  /**
   * Override parent cleanup to include unified subscriptions
   */
  override unsubscribeAll() {
    super.unsubscribeAll();
    this.unsubscribeAllUnified();
  }
} 